import { Component, OnInit, Input, HostBinding, ViewChild, ElementRef, HostListener } from '@angular/core';
import { IUProperty } from 'src/app/shared/interface/umbraco-property';
import { IPictureData } from 'src/app/shared/components/picture/picture.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RecaptchaComponent } from 'ng-recaptcha';
import { IRteStyleSettings } from '../../shared/interface/rte-style-settings';
import { applyRteSettings } from '../../shared/utils/rte-style-settings-utils';
import { ITitleStyleSettings } from '../../shared/interface/title-style-settings';
import { applyTitleSettings } from '../../shared/utils/title-style-settings-utils';

interface IBoardDemoPanelData {
  title?: IUProperty<string>;
  capitalizeTitle?: IUProperty<boolean>;
  description?: IUProperty<string>;
  backgroundImage: IUProperty<IPictureData>
  mailType?: IUProperty<number>;
  cookiePolicyPageUrl: string;
  reCaptchaSecret: string;

  rteStyleSettings?: IRteStyleSettings;
  titleStyleSettings?: ITitleStyleSettings;
  panelSettings?: any;
}

interface IBoardDemoFormPanelResponseData {
  reCaptchaFailed: boolean;
}

@Component({
  selector: 'app-board-demo-panel',
  templateUrl: './board-demo-panel.component.html',
  styleUrls: ['./board-demo-panel.component.less']
})
export class BoardDemoPanelComponent implements OnInit {
  @Input() data: IBoardDemoPanelData;
  @ViewChild('panelEl', { static: false }) panelEl: ElementRef<HTMLElement>;
  @ViewChild('titleEl', { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild('descriptionEl', { static: false }) descriptionEl: ElementRef<HTMLElement>;
  @ViewChild('captcha', { static: false }) captcha: RecaptchaComponent;
  scrollPosition: number = null;

  @HostListener('window:scroll', []) onWindowScroll() {
    this.scrollPosition = window.pageYOffset;
    if ((this.scrollPosition + window.outerHeight) > this.panelEl.nativeElement.offsetTop) {
      this.loadMedia();
    }
  }

  private alive$ = new Subject();

  formText = {
    name: this.translateService.instant("demoPanel.InputField.Name"),
    email: this.translateService.instant("demoPanel.InputField.Email"),
    phone: this.translateService.instant("demoPanel.InputField.Phone"),
    company: this.translateService.instant("demoPanel.InputField.Company"),
    submit: this.translateService.instant("demoPanel.SubmitButton.Send")
  }
  errMsg: string = null;
  succMsg: string = null;
  captchaResponse: string = null;
  requestSuccess: boolean = false;
  demoGroup;

  constructor(private http: HttpClient,
              private translateService: TranslateService) { }

  ngOnInit() {
    this.scrollPosition = window.pageYOffset;
    this.demoGroup = new FormGroup({
      name: new FormControl("", Validators.required),
      email: new FormControl("", Validators.compose([Validators.required, Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')])),
      phone: new FormControl("", Validators.required),
      company: new FormControl("", Validators.required),
      privacyPolicy: new FormControl(false, Validators.requiredTrue)
    });
  }

  reCaptchaValidated(response: string){
    this.captchaResponse = response;
  }
    
  resetCaptcha(){
    this.captcha.reset();
    this.captchaResponse = null;
  }

  ngAfterViewInit(): void {
    if (this.data.capitalizeTitle.value) {
      this.titleEl.nativeElement.style.textTransform = 'uppercase';
    }
    applyTitleSettings(this.data, this.titleEl);
    applyRteSettings(this.data, this.descriptionEl)

    if ((this.scrollPosition + window.outerHeight) > this.panelEl.nativeElement.offsetTop) {
      this.loadMedia();
    }
  }

  loadMedia() {
    if (this.data && this.data.backgroundImage && this.data.backgroundImage.value && this.panelEl && this.panelEl.nativeElement) {
      this.panelEl.nativeElement.style.backgroundImage = 'url(' + this.data.backgroundImage.value.src + ')';
      this.panelEl.nativeElement.style.backgroundSize = 'cover';
      this.panelEl.nativeElement.style.backgroundRepeat = 'no-repeat';
    }
  }

  sendRequest(event) {
    event.preventDefault();
    this.errMsg = "";

    if (!this.demoGroup.invalid && this.captchaResponse) {
      const data = {...this.demoGroup.value};
      data.mailType = this.data.mailType.value;
      data.reCaptchaToken = this.captchaResponse;
      this.http.post('ubaseline/api/demoFormPanel/sendRequest', data).subscribe(
        (data: IBoardDemoFormPanelResponseData)  => { 
          if(data.reCaptchaFailed){
            this.errMsg = this.translateService.instant("reCaptcha.Error");
            this.resetCaptcha();
            return;
          }

          this.demoGroup.reset({
            name: '', 
            email: '', 
            phone: '', 
            company: '', 
            privacyPolicy: false, 
          }), 
          this.demoGroup.markAsUntouched();
          this.demoGroup.markAsPristine();
          this.successOnSend() 
        },
        error => console.log('An error has occured', error)
      )
    } else {
      if (!this.demoGroup.value.privacyPolicy) {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.PrivacyPolicy");
      } else if(!this.captchaResponse) {
        this.errMsg = this.translateService.instant("reCaptcha.CheckRecaptcha");
      } else if (this.demoGroup.controls.name.status === "INVALID") {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.MissingName");
      } else if (this.demoGroup.controls.email.status === "INVALID") {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.Email");
      } else if (this.demoGroup.controls.phone.status === "INVALID") {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.MissingPhone");
      } else if (this.demoGroup.controls.company.status === "INVALID") {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.MissingCompany");
      }
      }
    }
  successOnSend() {
    this.requestSuccess = true;
    this.succMsg = this.translateService.instant("demoPanel.SuccessMessage.OnSend");
    this.resetCaptcha();
    this.startTimer();
  }

  startTimer() {
    let countDown;
    let timeLeft: number = 5;
    const source = timer(1000, 1000);
    countDown = source.pipe(takeUntil(this.alive$)).subscribe(val => {
      timeLeft--;
      if (timeLeft === 0) {
        this.requestSuccess = false;
        countDown.unsubscribe();
      }
    });
  }

  ngOnDestroy() {
    this.alive$.next();
    this.alive$.complete();
  }
}
